import {
  AlertTriangle,
  ClipboardCheck,
  CreditCard,
  FileWarning,
  GraduationCap,
  Layers3,
  Laptop,
  MessageSquareWarning,
  UserCheck,
  UserX,
  Users,
} from "lucide-react";
import type { MembershipStats } from "../types";

function MiniCard({
  label,
  value,
  icon: Icon,
  tone,
  alert,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  tone: string;
  alert?: boolean;
}) {
  return (
    <div
      className={`rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        alert && value > 0 ? "border-red-200" : "border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{label}</p>
          <h3
            className={`mt-2 text-3xl font-black ${
              alert && value > 0 ? "text-red-700" : "text-slate-900"
            }`}
          >
            {value}
          </h3>
        </div>
        <div className={`rounded-2xl border p-3 ${tone}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

export function MembershipSummaryCards({ stats }: { stats: MembershipStats }) {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {/* Pipeline counts */}
      <MiniCard
        label="Total Members"
        value={stats.totalMembers}
        icon={Users}
        tone="border-emerald-100 bg-emerald-50 text-emerald-700"
      />
      <MiniCard
        label="Active Members"
        value={stats.activeMembers}
        icon={UserCheck}
        tone="border-blue-100 bg-blue-50 text-blue-700"
      />
      <MiniCard
        label="Total Applications"
        value={stats.totalApplications}
        icon={ClipboardCheck}
        tone="border-amber-100 bg-amber-50 text-amber-700"
      />
      <MiniCard
        label="Pending Applications"
        value={stats.pendingApplications}
        icon={ClipboardCheck}
        tone="border-amber-100 bg-amber-50 text-amber-700"
        alert
      />

      {/* Workflow stages */}
      <MiniCard
        label="Prospects"
        value={stats.prospects}
        icon={Users}
        tone="border-slate-100 bg-slate-50 text-slate-700"
      />
      <MiniCard
        label="Board Pending"
        value={stats.boardPending}
        icon={Layers3}
        tone="border-purple-100 bg-purple-50 text-purple-700"
        alert
      />
      <MiniCard
        label="Payment Pending"
        value={stats.paymentPending}
        icon={CreditCard}
        tone="border-orange-100 bg-orange-50 text-orange-700"
        alert
      />
      <MiniCard
        label="ICT Pending"
        value={stats.ictPending}
        icon={Laptop}
        tone="border-sky-100 bg-sky-50 text-sky-700"
        alert
      />
      <MiniCard
        label="Training Pending"
        value={stats.trainingPending}
        icon={GraduationCap}
        tone="border-teal-100 bg-teal-50 text-teal-700"
        alert
      />
      <MiniCard
        label="Suspended"
        value={stats.suspendedMembers}
        icon={UserX}
        tone="border-red-100 bg-red-50 text-red-700"
        alert
      />

      {/* Attention items */}
      <MiniCard
        label="Overdue Payments"
        value={stats.overduePayments}
        icon={AlertTriangle}
        tone="border-red-100 bg-red-50 text-red-700"
        alert
      />
      <MiniCard
        label="Pending Documents"
        value={stats.pendingDocuments}
        icon={FileWarning}
        tone="border-yellow-100 bg-yellow-50 text-yellow-700"
        alert
      />
      <MiniCard
        label="Pending Follow-ups"
        value={stats.pendingTradeFollowUps}
        icon={MessageSquareWarning}
        tone="border-indigo-100 bg-indigo-50 text-indigo-700"
        alert
      />
    </div>
  );
}
